import { useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { Button } from '@/shared/ui/button';
import { ErrorState } from '@/shared/feedback';
import type { ProviderSummary } from '@/api/schemas/ai';
import {
  ProviderCapabilityFields,
  ProviderConnectionFields,
  ProviderGenerationFields,
} from './provider-form-fields';
import type {
  ProviderCapabilityChange,
  ProviderFormChange,
  ProviderFormErrors,
  ProviderFormProps,
  ProviderFormResult,
  ProviderFormState,
} from './provider-form-types';

/**
 * The provider form, used by the platform console AND the tenant workspace.
 *
 * ---------------------------------------------------------------------------
 * THE CREDENTIAL FIELD IS WRITE-ONLY
 * ---------------------------------------------------------------------------
 * The credential is a password input. It is emptied the moment the form is
 * submitted and the value is never written back into the form state, never
 * rendered outside the input, and never kept in a variable that outlives the
 * submit. The API does not return it, so nothing can re-populate it.
 *
 * Leaving it blank is meaningful: the form does not send a credential, and
 * whatever key is already stored stays in place.
 */
export function ProviderForm(props: ProviderFormProps): ReactNode {
  const [values, setValues] = useState<ProviderFormState>(props.initialValues);
  const [apiKey, setApiKey] = useState('');
  const [errors, setErrors] = useState<ProviderFormErrors>({});

  const update: ProviderFormChange = (key, value) => {
    setValues((previous) => ({ ...previous, [key]: value }));
  };

  const updateCapability: ProviderCapabilityChange = (key, checked) => {
    setValues((previous) => ({
      ...previous,
      capabilities: { ...previous.capabilities, [key]: checked },
    }));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const nextErrors = validate(values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    props.onSubmit(buildResult(values, apiKey));

    // The key is not kept: it has left for the API and must not stay in the
    // form state, where a re-render could put it back on screen.
    setApiKey('');
  };

  return (
    <form className="space-y-6" onSubmit={handleSubmit} noValidate>
      {props.submitError ? (
        <ErrorState title="Could not save" message={props.submitError.message} />
      ) : null}

      <ProviderConnectionFields
        values={values}
        errors={errors}
        apiKey={apiKey}
        credentialLabel={props.credentialLabel}
        credentialHint={props.credentialHint}
        onChange={update}
        onApiKeyChange={setApiKey}
      />

      <ProviderGenerationFields values={values} errors={errors} onChange={update} />

      <ProviderCapabilityFields values={values} onCapabilityChange={updateCapability} />

      <div className="flex gap-2">
        <Button type="submit" loading={props.isSubmitting}>
          {props.submitLabel}
        </Button>
        {props.onCancel ? (
          <Button type="button" variant="secondary" onClick={props.onCancel}>
            Cancel
          </Button>
        ) : null}
      </div>
    </form>
  );
}

/** A blank form, for creating a provider. */
export function emptyProviderFormState(): ProviderFormState {
  return {
    name: '',
    baseUrl: '',
    modelId: '',
    timeoutMs: '',
    maxOutputTokens: '',
    temperature: '',
    capabilities: {
      structuredOutput: false,
      jsonMode: false,
      tools: false,
      modelDiscovery: false,
    },
  };
}

/** The form state that edits an existing provider. */
export function providerFormStateFrom(provider: ProviderSummary): ProviderFormState {
  return {
    name: provider.name,
    baseUrl: provider.baseUrl,
    modelId: provider.modelId,
    timeoutMs: String(provider.timeoutMs),
    maxOutputTokens: provider.maxOutputTokens === null ? '' : String(provider.maxOutputTokens),
    temperature: provider.temperature === null ? '' : String(provider.temperature),
    capabilities: provider.capabilities,
  };
}

/**
 * The values the console refuses to send.
 *
 * THESE MIRROR THE API'S SCHEMA, and that is the point: the API requires a timeout and
 * bounds all three numbers, so a form that let an absent or out-of-range value through
 * produced a request the API answered `400 ai.provider.invalid_request` — a refusal that
 * names neither the field nor the reason. A refusal the operator can act on is a field
 * error on the field.
 *
 * The timeout is REQUIRED because the API requires it: there is no default for it, and
 * the console does not invent one. The other two are optional — absent means "the
 * provider's default" — but a value that IS present must be inside the API's bounds.
 */
/** The request timeout. The API requires it, and the console does not invent one. */
function validateTimeout(raw: string): string | undefined {
  const timeout = parseOptionalNumber(raw);
  if (timeout === undefined) return 'A timeout is required.';
  if (!Number.isInteger(timeout)) return 'The timeout must be a whole number of milliseconds.';
  if (timeout < 1_000 || timeout > 600_000) {
    return 'The timeout must be between 1000 and 600000 milliseconds.';
  }
  return undefined;
}

/** The maximum output tokens. Absent means "the provider's default"; present means bounded. */
function validateMaxOutputTokens(raw: string): string | undefined {
  if (raw.trim() === '') return undefined;
  const tokens = parseOptionalNumber(raw);
  if (tokens === undefined || !Number.isInteger(tokens)) {
    return 'The maximum output tokens must be a whole number.';
  }
  if (tokens < 1 || tokens > 131_072) {
    return 'The maximum output tokens must be between 1 and 131072.';
  }
  return undefined;
}

/** The temperature. Absent means "the provider's default"; present means bounded. */
function validateTemperature(raw: string): string | undefined {
  if (raw.trim() === '') return undefined;
  const temperature = parseOptionalNumber(raw);
  if (temperature === undefined) return 'The temperature must be a number.';
  if (temperature < 0 || temperature > 2) return 'The temperature must be between 0 and 2.';
  return undefined;
}

/**
 * The three numbers, checked against the API's bounds.
 *
 * Split out from `validate` so each function stays legible: the text fields are one
 * question, and the numbers are another.
 */
function validateNumbers(values: ProviderFormState): ProviderFormErrors {
  const errors: ProviderFormErrors = {};

  const timeout = validateTimeout(values.timeoutMs);
  if (timeout !== undefined) errors.timeoutMs = timeout;

  const maxOutputTokens = validateMaxOutputTokens(values.maxOutputTokens);
  if (maxOutputTokens !== undefined) errors.maxOutputTokens = maxOutputTokens;

  const temperature = validateTemperature(values.temperature);
  if (temperature !== undefined) errors.temperature = temperature;

  return errors;
}

function validate(values: ProviderFormState): ProviderFormErrors {
  const errors: ProviderFormErrors = {};
  if (values.name.trim() === '') errors.name = 'A name is required.';
  if (values.baseUrl.trim() === '') errors.baseUrl = 'A base URL is required.';
  if (values.modelId.trim() === '') errors.modelId = 'A model identifier is required.';
  return { ...errors, ...validateNumbers(values) };
}

/** Turns the form state into the request body, and the key into its own field. */
function buildResult(values: ProviderFormState, apiKey: string): ProviderFormResult {
  const trimmedKey = apiKey.trim();
  return {
    provider: {
      name: values.name.trim(),
      baseUrl: values.baseUrl.trim(),
      modelId: values.modelId.trim(),
      timeoutMs: parseOptionalNumber(values.timeoutMs),
      maxOutputTokens: parseOptionalNumber(values.maxOutputTokens) ?? null,
      temperature: parseOptionalNumber(values.temperature) ?? null,
      capabilities: values.capabilities,
    },
    apiKey: trimmedKey === '' ? null : trimmedKey,
  };
}

/** A number, or undefined when the field is blank or not a number. */
function parseOptionalNumber(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (trimmed === '') return undefined;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
}
